import {
    Button,
    createStyles,
    Modal,
    Paper,
    Theme,
    Typography,
    withStyles,
    WithStyles
} from "@material-ui/core";
import { useSnackbar } from "notistack";
import { useResetTermsAcceptanceMutation } from "../../api/accountApiSlice";
import LoaderButton from "../shared/LoaderButton";

type ResetTermsAcceptanceDialogConfirmationProps = WithStyles<typeof styles> & {
    visible: boolean;
    closeDialog: () => void;
};

const styles = (theme: Theme) =>
    createStyles({
        body: {
            [theme.breakpoints.down("xs")]: { padding: theme.spacing(2) },
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: theme.spacing(4),
            textAlign: "center"
        },
        buttonWrapper: {
            display: "flex",
            flexDirection: "row",
            rowGap: theme.spacing(1.5),
            columnGap: theme.spacing(1.5),
            [theme.breakpoints.down("sm")]: {
                flexDirection: "column"
            }
        },
        bodyText: {
            [theme.breakpoints.down("xs")]: { fontSize: 16 },
            color: theme.palette.text.secondary,
            lineHeight: 1.5,
            marginBottom: theme.spacing(3),
            textAlign: "center"
        },
        header: {
            borderBottom: `8px solid ${theme.palette.primary.main}`,
            padding: theme.spacing(2),
            textAlign: "center",
            width: "100%"
        },
        headerText: {
            [theme.breakpoints.down("xs")]: { fontSize: 20 },
            color: theme.palette.primary.main,
            fontWeight: "bold",
            margin: "0 auto"
        },
        paper: {
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[5],
            margin: theme.spacing(2),
            maxWidth: 600,
            outline: "none",
            width: "100%"
        }
    });

function ResetTermsAcceptanceDialog({
    classes,
    closeDialog,
    visible
}: ResetTermsAcceptanceDialogConfirmationProps) {
    const { enqueueSnackbar } = useSnackbar();

    const [resetTermsAcceptanceMutation, resetTermsAcceptanceMutationData] =
        useResetTermsAcceptanceMutation();

    const onReset = async () => {
        try {
            await resetTermsAcceptanceMutation().unwrap();

            enqueueSnackbar(
                "Successfully reset Terms and Conditions acceptance for all users",
                {
                    anchorOrigin: { horizontal: "left", vertical: "top" },
                    preventDuplicate: false,
                    variant: "success"
                }
            );

            closeDialog();
        } catch (e) {
            console.error(e);

            enqueueSnackbar(
                "Failed to reset Terms and Conditions acceptance for all users",
                {
                    anchorOrigin: { horizontal: "left", vertical: "top" },
                    preventDuplicate: false,
                    variant: "error"
                }
            );
        }
    };

    return (
        <Modal open={visible} className={classes.body}>
            <Paper className={classes.paper} elevation={2}>
                <div className={classes.header}>
                    <Typography
                        component="h1"
                        variant="h5"
                        className={classes.headerText}
                    >
                        Confirm Reset Terms and Conditions
                    </Typography>
                </div>
                <div className={classes.body}>
                    <Typography
                        component="div"
                        variant="h6"
                        className={classes.bodyText}
                    >
                        Are you sure you want to reset Terms and Conditions
                        acceptance for all users? This will require all users to
                        re-accept the next time they log in.
                    </Typography>

                    <div className={classes.buttonWrapper}>
                        <LoaderButton
                            color="primary"
                            type="button"
                            variant="contained"
                            onClick={onReset}
                            loading={resetTermsAcceptanceMutationData.isLoading}
                        >
                            Confirm
                        </LoaderButton>
                        <Button
                            color="secondary"
                            type="button"
                            variant="contained"
                            onClick={closeDialog}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            </Paper>
        </Modal>
    );
}

export default withStyles(styles)(ResetTermsAcceptanceDialog);
